import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell,
  Send,
  History,
  FileText,
  Settings as SettingsIcon,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Loader,
  CheckCircle,
  AlertCircle,
  Users,
  RefreshCw,
  Wrench,
  UserRound,
  Briefcase,
  Globe2,
  Sparkles,
  Smartphone,
  Eye,
  Radio,
} from 'lucide-react';
import { NotificationService } from '../../services/notificationService';
import NotificationAiStudio from './NotificationAiStudio';

const CATEGORIES = [
  { id: 'system', name: 'System', color: '#0d9488' },
  { id: 'service_request', name: 'Service Request', color: '#f59e0b' },
  { id: 'payment', name: 'Payment', color: '#ef4444' },
  { id: 'reminder', name: 'Reminder', color: '#8b5cf6' },
  { id: 'chat', name: 'Chat', color: '#3b82f6' },
  { id: 'shipment', name: 'Shipment', color: '#0891b2' },
  { id: 'order', name: 'Order', color: '#d97706' },
  { id: 'delivery', name: 'Delivery', color: '#16a34a' },
  { id: 'booking', name: 'Booking', color: '#7c3aed' },
];

const AUDIENCE_SEGMENTS = [
  {
    id: 'customer',
    name: 'Customer',
    description: 'Members with the RoadServe app',
    icon: UserRound,
    accent: '#0f766e',
  },
  {
    id: 'mechanic',
    name: 'Mechanic',
    description: 'Partner roadside app',
    icon: Wrench,
    accent: '#c2410c',
  },
  {
    id: 'staff',
    name: 'Staff',
    description: 'Telecallers, advisors, admins',
    icon: Briefcase,
    accent: '#6d28d9',
  },
  {
    id: 'all',
    name: 'Everyone',
    description: 'Every installed flavor',
    icon: Globe2,
    accent: '#1d4ed8',
  },
];

const TARGET_ROLE_LABELS = {
  all: 'Everyone',
  customer: 'Customers',
  mechanic: 'Mechanics',
  staff: 'Staff',
  telecaller: 'Telecallers',
  serviceexecutive: 'Service Executives',
  serviceadvisor: 'Service Advisors',
  teamlead: 'Team Leads',
  admin: 'Admins',
};

const TABS = [
  { id: 'send', label: 'Compose', icon: Send },
  { id: 'history', label: 'History', icon: History },
  { id: 'templates', label: 'Templates', icon: FileText },
  { id: 'ai', label: 'AI Studio', icon: Sparkles },
  { id: 'preferences', label: 'Categories', icon: SettingsIcon },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getCategoryStyle(cat) {
  const c = CATEGORIES.find((x) => x.id === cat);
  if (!c) return { backgroundColor: '#f1f5f9', color: '#64748b' };
  return { backgroundColor: `${c.color}18`, color: c.color };
}

function BroadcastMetrics({ item }) {
  const sent = Number(item.inbox_count ?? item.recipient_count ?? 0);
  const delivered = Number(item.push_ok ?? item.push_count ?? 0);
  const failed = Number(item.push_fail ?? 0);
  const opened = Number(item.read_count ?? 0);
  const unread = Number(item.unread_count ?? Math.max(sent - opened, 0));
  const openPct = sent > 0 ? Math.round((opened / sent) * 100) : 0;
  const deliverPct = sent > 0 ? Math.round((delivered / sent) * 100) : 0;
  const noPushRecorded = delivered === 0 && failed === 0;
  const oldInboxDump = noPushRecorded && sent >= 80;

  return (
    <div className="mt-4 space-y-3">
      {oldInboxDump && (
        <p className="text-[12px] leading-relaxed rounded-xl bg-amber-50 text-amber-900 px-3 py-2 border border-amber-100">
          Purani inbox dump — is send pe phone delivery record nahi hai. Nayi compose ke baad yahan
          Delivered / Opened alag dikhega.
        </p>
      )}
      {!oldInboxDump && delivered === 0 && failed > 0 && (
        <p className="text-[12px] leading-relaxed rounded-xl bg-rose-50 text-rose-900 px-3 py-2 border border-rose-100">
          Inbox row ban gayi, lekin FCM/Expo fail: {failed} tokens. Customer FCM secrets aur function
          deploy check karo.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricChip label="Inbox" value={sent} />
        <MetricChip label="Delivered" value={delivered} tone="teal" />
        <MetricChip label="Opened" value={`${opened} · ${openPct}%`} tone="violet" />
        <MetricChip label="Unread" value={unread} />
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden flex">
        <div className="h-full bg-teal-500" style={{ width: `${Math.min(deliverPct, 100)}%` }} />
        <div className="h-full bg-violet-500" style={{ width: `${Math.min(openPct, 100 - deliverPct)}%` }} />
      </div>
    </div>
  );
}

function MetricChip({ label, value, tone }) {
  const tones = {
    teal: 'bg-teal-50 text-teal-900',
    violet: 'bg-violet-50 text-violet-900',
  };
  return (
    <div className={`rounded-xl px-3 py-2 ${tones[tone] || 'bg-slate-50 text-slate-800'}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-sm font-semibold mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}

function PhonePreview({ title, message, audience }) {
  return (
    <div className="mx-auto w-[240px]">
      <div className="rounded-[2rem] border-[6px] border-slate-900 bg-slate-900 shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-b from-slate-800 to-slate-950 px-3 pt-3 pb-8 min-h-[280px]">
          <div className="mx-auto mb-4 h-1.5 w-16 rounded-full bg-slate-700" />
          <div className="rounded-2xl bg-white/95 backdrop-blur p-3 shadow-lg">
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-teal-600 text-white flex items-center justify-center shrink-0 text-[10px] font-bold">
                RS
              </div>
              <div className="min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold text-slate-500">ROADSERVE</p>
                  <p className="text-[9px] text-slate-400">now</p>
                </div>
                <p className="text-[13px] font-semibold text-slate-900 leading-snug mt-0.5 line-clamp-2">
                  {title || 'Notification title'}
                </p>
                <p className="text-[11px] text-slate-600 mt-0.5 line-clamp-3">
                  {message || 'Message yahan live preview mein dikhega'}
                </p>
              </div>
            </div>
          </div>
          <p className="text-center text-[10px] text-slate-500 mt-4">
            {audience} · device default tone
          </p>
        </div>
      </div>
    </div>
  );
}

const Notifications = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('system');
  const [selectedTargetRole, setSelectedTargetRole] = useState('customer');

  const [templateTitle, setTemplateTitle] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');
  const [templateCategory, setTemplateCategory] = useState('system');
  const [templateTargetRole, setTemplateTargetRole] = useState('customer');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState({ dbUsers: 0, appInstalls: 0 });

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
    } catch {
      return {};
    }
  })();

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await NotificationService.getBroadcastHistory(50, 0);
      setBroadcastHistory(data);
    } catch (e) {
      console.error('Failed to load history', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const data = await NotificationService.listTemplates();
      setTemplates(data);
    } catch (e) {
      console.error('Failed to load templates', e);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
    loadTemplates();
  }, [loadHistory, loadTemplates]);

  useEffect(() => {
    let cancelled = false;
    NotificationService.getAudiencePreview(selectedTargetRole)
      .then((preview) => {
        if (!cancelled) setAudiencePreview(preview);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedTargetRole]);

  const handleSendNotification = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      setSendResult({ type: 'error', message: 'Title aur message dono required hain' });
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const result = await NotificationService.sendBroadcast({
        title: notificationTitle.trim(),
        message: notificationMessage.trim(),
        category: selectedCategory,
        targetRole: selectedTargetRole,
        sentBy: currentUser?.id || null,
      });

      if (result.success) {
        const audience = TARGET_ROLE_LABELS[selectedTargetRole] || selectedTargetRole;
        const delivered = result.pushOk ?? 0;
        const failed = result.pushFail ?? 0;
        setSendResult({
          type: delivered > 0 ? 'success' : 'error',
          message:
            delivered > 0
              ? `${audience} — ${delivered} phones pe gayi${failed ? `, ${failed} fail` : ''}. Opened History mein tap ke baad aayega.`
              : `${audience} — inbox ${result.sent || 0}, phone pe 0. notification-broadcast-send deploy karo aur FCM_CUSTOMER_* secrets check karo.`,
        });
        if (delivered > 0) {
          setNotificationTitle('');
          setNotificationMessage('');
          setSelectedCategory('system');
        }
        loadHistory();
      } else {
        setSendResult({ type: 'error', message: result.error || 'Send karne mein error aaya' });
      }
    } catch (e) {
      setSendResult({ type: 'error', message: e.message || 'Send karne mein error aaya' });
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateTitle.trim() || !templateMessage.trim()) return;
    setSavingTemplate(true);
    try {
      const payload = {
        title: templateTitle.trim(),
        message: templateMessage.trim(),
        category: templateCategory,
        targetRole: templateTargetRole,
      };
      if (editingTemplate) payload.id = editingTemplate.id;

      const result = await NotificationService.upsertTemplate(payload);
      if (result) {
        setShowTemplateModal(false);
        setTemplateTitle('');
        setTemplateMessage('');
        setEditingTemplate(null);
        loadTemplates();
      }
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Kya aap ye template delete karna chahte hain?')) return;
    await NotificationService.deleteTemplate(id);
    loadTemplates();
  };

  const useTemplate = (template) => {
    setNotificationTitle(template.title || '');
    setNotificationMessage(template.message || '');
    setSelectedCategory(template.category || 'system');
    const role = template.target_role || 'customer';
    setSelectedTargetRole(AUDIENCE_SEGMENTS.some((s) => s.id === role) ? role : 'customer');
    setSendResult(null);
    setActiveTab('send');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getAudienceLabel = (role) => TARGET_ROLE_LABELS[role] || role || '—';
  const selectedAudience = AUDIENCE_SEGMENTS.find((s) => s.id === selectedTargetRole);
  const latest = broadcastHistory[0];
  const latestOpened = Number(latest?.read_count || 0);
  const latestSent = Number(latest?.inbox_count ?? latest?.recipient_count ?? 0);

  return (
    <div className="-mx-4 -mt-4 sm:-mx-5 sm:-mt-5 lg:-mx-6 lg:-mt-6 -mb-8 min-h-full bg-[#f4f1ea]">
      <div className="bg-slate-950 text-white px-6 pt-7 pb-5">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-teal-300/90">RoadServe comms</p>
            <h1 className="text-3xl font-semibold tracking-tight mt-1">Broadcasts</h1>
            <p className="text-slate-400 text-sm mt-1.5 max-w-xl">
              Sirf app-installs ko push. Customer tone device default hai — mechanic job ring nahi.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 min-w-[120px]">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Installs</p>
              <p className="text-xl font-semibold tabular-nums mt-0.5">{audiencePreview.appInstalls}</p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 min-w-[120px]">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Last opened</p>
              <p className="text-xl font-semibold tabular-nums mt-0.5">
                {latestSent ? `${latestOpened}/${latestSent}` : '—'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-1 overflow-x-auto rounded-2xl bg-white/5 p-1 border border-white/10">
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                  active ? 'bg-white text-slate-950 shadow' : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <tab.icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {activeTab === 'send' && (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-8 space-y-5">
              <div className="bg-white rounded-[1.75rem] border border-stone-200/80 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] overflow-hidden">
                <div className="px-6 py-5 border-b border-stone-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Compose</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Audience choose karo, likho, Send.</p>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-teal-800 bg-teal-50 px-3 py-1 rounded-full">
                    <Radio size={12} />
                    Live FCM
                  </span>
                </div>

                <div className="p-6 space-y-5">
                  {sendResult && (
                    <div
                      className={`p-4 rounded-2xl flex items-start gap-3 text-sm ${
                        sendResult.type === 'success'
                          ? 'bg-teal-50 text-teal-950 border border-teal-100'
                          : 'bg-rose-50 text-rose-950 border border-rose-100'
                      }`}
                    >
                      {sendResult.type === 'success' ? (
                        <CheckCircle size={18} className="mt-0.5 shrink-0" />
                      ) : (
                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                      )}
                      <span className="font-medium leading-relaxed">{sendResult.message}</span>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Send to
                    </label>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                      {AUDIENCE_SEGMENTS.map((segment) => {
                        const Icon = segment.icon;
                        const active = selectedTargetRole === segment.id;
                        return (
                          <button
                            key={segment.id}
                            type="button"
                            onClick={() => {
                              setSelectedTargetRole(segment.id);
                              setSendResult(null);
                            }}
                            className={`text-left rounded-2xl border p-3.5 transition ${
                              active
                                ? 'border-slate-900 bg-slate-950 text-white shadow-md'
                                : 'border-stone-200 bg-stone-50 hover:border-stone-300'
                            }`}
                          >
                            <Icon size={18} className={active ? 'text-teal-300' : 'text-slate-500'} />
                            <p className="font-semibold text-sm mt-2">{segment.name}</p>
                            <p className={`text-[11px] mt-0.5 leading-snug ${active ? 'text-slate-400' : 'text-slate-500'}`}>
                              {segment.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-2xl bg-stone-50 border border-stone-100 px-4 py-3 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                      <Smartphone size={14} />
                      {audiencePreview.appInstalls} app installs
                    </span>
                    <span className="text-slate-400">inhe push jayegi</span>
                    {audiencePreview.dbUsers > audiencePreview.appInstalls ? (
                      <span className="text-slate-500">
                        · {audiencePreview.dbUsers} DB records skip (bina app)
                      </span>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={notificationTitle}
                      onChange={(e) => setNotificationTitle(e.target.value)}
                      className="w-full px-4 py-3 border border-stone-200 rounded-2xl bg-white focus:ring-2 focus:ring-teal-600/30 focus:border-teal-700 text-[15px]"
                      placeholder="e.g. Night breakdown? We’re on it"
                      maxLength={120}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Message
                      </label>
                      <span className="text-[11px] text-slate-400 tabular-nums">
                        {notificationMessage.length}/500
                      </span>
                    </div>
                    <textarea
                      value={notificationMessage}
                      onChange={(e) => setNotificationMessage(e.target.value)}
                      rows={5}
                      className="w-full px-4 py-3 border border-stone-200 rounded-2xl bg-white focus:ring-2 focus:ring-teal-600/30 focus:border-teal-700 resize-y text-[15px]"
                      placeholder="Short, clear, one action..."
                      maxLength={500}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Category
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map((cat) => {
                        const active = selectedCategory === cat.id;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => setSelectedCategory(cat.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                              active ? 'text-white border-transparent' : 'bg-white text-slate-600 border-stone-200 hover:border-stone-300'
                            }`}
                            style={active ? { backgroundColor: cat.color } : undefined}
                          >
                            {cat.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                    <p className="text-sm text-slate-500">
                      Sending to{' '}
                      <span className="font-semibold text-slate-900">{selectedAudience?.name}</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setActiveTab('templates')}
                        className="px-4 py-2.5 border border-stone-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-stone-50"
                      >
                        Templates
                      </button>
                      <button
                        type="button"
                        onClick={handleSendNotification}
                        disabled={sending}
                        className="px-5 py-2.5 bg-teal-700 text-white rounded-xl hover:bg-teal-800 flex items-center gap-2 disabled:opacity-50 text-sm font-semibold shadow-sm"
                      >
                        {sending ? (
                          <>
                            <Loader size={16} className="animate-spin" />
                            Sending
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            Send now
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="xl:col-span-4 space-y-5">
              <div className="bg-white rounded-[1.75rem] border border-stone-200/80 p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Preview</p>
                <PhonePreview
                  title={notificationTitle}
                  message={notificationMessage}
                  audience={selectedAudience?.name}
                />
              </div>

              <div className="bg-white rounded-[1.75rem] border border-stone-200/80 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-slate-900">Recent</h2>
                  <button
                    onClick={loadHistory}
                    className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-stone-50"
                    title="Refresh"
                  >
                    <RefreshCw size={15} />
                  </button>
                </div>
                {loadingHistory ? (
                  <div className="flex justify-center py-8">
                    <Loader size={20} className="animate-spin text-teal-700" />
                  </div>
                ) : broadcastHistory.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">Abhi koi broadcast nahi</p>
                ) : (
                  <div className="space-y-2">
                    {broadcastHistory.slice(0, 5).map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setActiveTab('history')}
                        className="w-full text-left rounded-2xl border border-stone-100 hover:border-stone-200 p-3"
                      >
                        <p className="text-sm font-medium text-slate-900 line-clamp-1">{item.title}</p>
                        <p className="text-[11px] text-slate-500 mt-1">
                          {item.push_ok ?? 0} delivered · {item.read_count ?? 0} opened · {formatDate(item.created_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">History</h2>
                <p className="text-sm text-slate-500 mt-0.5">Opened = user ne tray ya in-app notification tap ki</p>
              </div>
              <button
                onClick={loadHistory}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-700 border border-stone-200 rounded-xl bg-white hover:bg-stone-50"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
            </div>

            {loadingHistory ? (
              <div className="flex justify-center py-16">
                <Loader size={28} className="animate-spin text-teal-700" />
              </div>
            ) : broadcastHistory.length === 0 ? (
              <div className="bg-white rounded-[1.75rem] p-14 border border-stone-200 text-center">
                <Bell size={40} className="mx-auto text-stone-300 mb-3" />
                <p className="text-slate-600">Koi broadcast history nahi hai</p>
              </div>
            ) : (
              broadcastHistory.map((item) => (
                <div key={item.id} className="bg-white rounded-[1.75rem] p-5 border border-stone-200/80 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600 mt-1 leading-relaxed">{item.message}</p>
                    </div>
                    <span
                      className="px-2.5 py-1 text-xs font-medium rounded-full shrink-0"
                      style={getCategoryStyle(item.category)}
                    >
                      {item.category}
                    </span>
                  </div>
                  <BroadcastMetrics item={item} />
                  <div className="flex items-center flex-wrap gap-3 text-xs text-slate-500 mt-4">
                    <span className="inline-flex items-center gap-1">
                      <Users size={13} />
                      {getAudienceLabel(item.target_role)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Eye size={13} />
                      {item.read_count ?? 0} opened
                    </span>
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-slate-900">Templates</h2>
              <div className="flex gap-2">
                <button onClick={loadTemplates} className="p-2 text-slate-400 hover:text-slate-700">
                  <RefreshCw size={18} />
                </button>
                <button
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateTitle('');
                    setTemplateMessage('');
                    setTemplateCategory('system');
                    setTemplateTargetRole('customer');
                    setShowTemplateModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-950 text-white rounded-xl hover:bg-slate-800"
                >
                  <Plus size={18} />
                  New template
                </button>
              </div>
            </div>

            {loadingTemplates ? (
              <div className="flex justify-center py-12">
                <Loader size={28} className="animate-spin text-teal-700" />
              </div>
            ) : templates.length === 0 ? (
              <div className="bg-white rounded-[1.75rem] p-12 border border-stone-200 text-center">
                <FileText size={40} className="mx-auto text-stone-300 mb-3" />
                <p className="text-slate-600">Koi template nahi hai</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <div key={template.id} className="bg-white rounded-[1.5rem] p-5 border border-stone-200">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <h3 className="font-semibold text-slate-900">{template.title}</h3>
                      <span
                        className="px-2 py-1 text-xs rounded-full shrink-0"
                        style={getCategoryStyle(template.category)}
                      >
                        {template.category}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-3">{template.message}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">{getAudienceLabel(template.target_role)}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => useTemplate(template)}
                          className="px-3 py-1 text-xs bg-teal-50 text-teal-800 rounded-lg"
                        >
                          Use
                        </button>
                        <button
                          onClick={() => {
                            setEditingTemplate(template);
                            setTemplateTitle(template.title);
                            setTemplateMessage(template.message);
                            setTemplateCategory(template.category || 'system');
                            setTemplateTargetRole(template.target_role || 'customer');
                            setShowTemplateModal(true);
                          }}
                          className="px-3 py-1 text-xs bg-slate-100 text-slate-700 rounded-lg"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="px-3 py-1 text-xs bg-rose-50 text-rose-700 rounded-lg"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'ai' && (
          <NotificationAiStudio
            currentUser={currentUser}
            categories={CATEGORIES}
            audienceSegments={AUDIENCE_SEGMENTS}
            targetRoleLabels={TARGET_ROLE_LABELS}
            onSent={loadHistory}
          />
        )}

        {activeTab === 'preferences' && (
          <div className="bg-white rounded-[1.75rem] p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-1">Categories</h2>
            <p className="text-slate-500 mb-6 text-sm">Compose ke time ye tags use hote hain</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center gap-3 p-3 bg-stone-50 rounded-2xl border border-stone-100"
                >
                  <div className="w-3.5 h-3.5 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{cat.name}</p>
                    <p className="text-xs text-slate-500">{cat.id}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showTemplateModal && (
        <div className="fixed inset-0 bg-slate-950/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[1.5rem] p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-900">
                {editingTemplate ? 'Edit template' : 'Create template'}
              </h2>
              <button onClick={() => setShowTemplateModal(false)} className="text-slate-400 hover:text-slate-700">
                <X size={22} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                <input
                  type="text"
                  value={templateTitle}
                  onChange={(e) => setTemplateTitle(e.target.value)}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl"
                  placeholder="Template title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <textarea
                  value={templateMessage}
                  onChange={(e) => setTemplateMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl"
                  placeholder="Template message"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                  <select
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                    className="w-full px-4 py-2.5 border border-stone-200 rounded-xl"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Audience</label>
                  <select
                    value={templateTargetRole}
                    onChange={(e) => setTemplateTargetRole(e.target.value)}
                    className="w-full px-4 py-2.5 border border-stone-200 rounded-xl"
                  >
                    {AUDIENCE_SEGMENTS.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 border border-stone-200 rounded-xl"
                  disabled={savingTemplate}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate}
                  className="px-4 py-2 bg-slate-950 text-white rounded-xl flex items-center gap-2 disabled:opacity-50"
                >
                  {savingTemplate ? <Loader size={18} className="animate-spin" /> : <Save size={18} />}
                  {editingTemplate ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
